import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import {
  ArrowRight,
  Phone,
  Eye,
  EyeOff,
  Sun,
  Moon,
  Home,
  Building2,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
} from "lucide-react";
import {
  GuideBody,
  GuideFaq,
  GuideRelated,
  CheckList,
  buildGuideSchemas,
  type GuideMeta,
} from "@/components/GuideLayout";
import { Reveal } from "@/components/Reveal";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";
import { LandingHeroConversion } from "@/components/LandingHeroConversion";

const meta: GuideMeta = {
  category: "Guide · Insynsskydd",
  title: "Insynsskydd för fönster — solfilm & spegelfilm som låter dig se ut men inte in",
  tldr:
    "Spegelfilm (även kallad solfilm med insynsskydd) fungerar som en envägsspegel dagtid: den ljusare sidan blir reflekterande och insynsskyddande, medan den mörkare sidan behåller fri utsikt. Funktionen kräver att utsidan är ljusare än insidan — vilket gäller under hela dagsljustimmen (≈07–19 i Sverige). Kvällstid, när inomhusbelysningen är starkare än utomhusljuset, vänds effekten och insyn kan uppstå. För kvällsinsyn rekommenderas kombination med gardiner eller en frostad dekorfilm. Pris: 750–1 600 kr per m² installerat.",
  readTimeMin: 8,
  path: "/insynsskydd-fonster",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Hur fungerar fönsterfilm som insynsskydd — så att man ser ut men inte in?",
      a: "Spegelfilm (eller solfilm med hög yttre reflektion) har en metalliserad eller keramisk yta som reflekterar bort en del av det inkommande ljuset. Eftersom utomhusljuset dagtid är 10–100 gånger starkare än inomhusbelysning, ser någon utifrån främst reflektionen av omgivningen (himmel, träd, husfasader) — medan du inifrån ser rakt ut, eftersom ditt öga är anpassat till det svagare inomhusljuset. Det är samma princip som en envägsspegel i förhörsrum.",
    },
    {
      q: "Fungerar insynsskyddet även på natten?",
      a: "Nej, spegelfilm skyddar inte mot insyn när det är mörkare ute än inne. Om du har tänt inomhus kan någon utifrån se in, även om en lätt reflektion finns kvar. Det gäller alla speglande filmer. För full insynsblockering oavsett ljusförhållanden behövs gardin eller persienn som komplement, eller frostad dekorfilm som är opak.",
    },
    {
      q: "Vad är skillnaden mellan spegelfilm och solfilm med insynsskydd?",
      a: "De tillhör samma produktfamilj och är båda solfilmer. Spegelfilm har högre yttre reflektionsgrad (40–65 %), vilket förstärker envägsspegeleffekten. \"Vanlig\" mörkare solfilm (t.ex. 3M Night Vision 35) ger ett visst insynsskydd dagtid men inte lika starkt. Klassisk neutral solfilm för rena energi/komfortändamål (3M Prestige) är optiskt klar och ger nästan inget insynsskydd alls.",
    },
    {
      q: "Finns det \"genomskinlig\" insynsskyddsfilm?",
      a: "En helt genomskinlig film kan inte blockera insyn. Det som menas är film som ser nästan klar ut inifrån (hög ljustransmission, 40–60 % VLT) men har starkt reflektion utifrån. Det är möjligt och vanligt, till exempel med 3M Silver 20 och liknande filmer.",
    },
    {
      q: "Påverkar insynsskyddsfilm utsikten inifrån?",
      a: "Ja, men skillnaden är liten. Vanlig spegelfilm med VLT 20–35 % gör utsikten lite mörkare, ungefär som lätt tonade solglasögon. Färg och kontrast bevaras. Inomhusljuset minskar 15–30 % vilket många upplever som behagligt under sommarhalvåret. Mer transparenta varianter (VLT 50–60 %) ger nästan obemärkt skillnad.",
    },
    {
      q: "Vad kostar insynsskyddande fönsterfilm?",
      a: "I Skåne 2026 ligger priset på 750–1 600 kr per m² installerat, beroende på filmkvalitet. Spegelfilm i standardklass (t.ex. 3M Silver) ligger runt 750–1 050 kr/m². Premiumfilm med keramisk teknik (3M Night Vision, Prestige Exterior) ligger runt 1 200–1 600 kr/m² och ger bättre färgneutralitet och längre livslängd. En villa-installation på 8 m² kostar typiskt 7 000–11 000 kr.",
    },
    {
      q: "Är spegelfilm tillåten i bostadsrätt eller hyresrätt?",
      a: "Invändigt monterad film räknas som lös inredning och kräver normalt inget styrelse­godkännande. Utvändig film, eller film som tydligt förändrar fasaden, kan kräva tillstånd. Invändiga varianter är diskreta och tekniskt likvärdiga i 95 % av fallen.",
    },
    {
      q: "Hur länge håller filmen?",
      a: "Invändig solfilm/spegelfilm har 15–20 års förväntad livslängd. 3M ger 10 års produktgaranti på material och vi lämnar 10 års arbetsgaranti på installationen. Filmen påverkar inte fönstrets garanti.",
    },
  ],
  related: [
    { to: "/solfilm-privat", label: "Solfilm för bostäder — översikt" },
    { to: "/solfilm-foretag", label: "Solfilm för företag" },
    { to: "/dekorfilm", label: "Dekorfilm / frostad film — opak insynsblockering" },
    { to: "/3m-prestige", label: "3M Prestige — klar premiumsolfilm" },
    { to: "/datablad", label: "Tekniska datablad — VLT, reflektion, TSER" },
    { to: "/auktoriserad-installator", label: "Hitta certifierad montör" },
  ],
};

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Så väljer du rätt insynsskydd för dina fönster",
  totalTime: "PT8M",
  step: [
    { "@type": "HowToStep", position: 1, name: "Bestäm när du behöver insynsskydd", text: "Främst dagtid (vanligast — då fungerar spegelfilm perfekt) eller dygnet runt (då krävs frostad dekorfilm eller film + gardin)." },
    { "@type": "HowToStep", position: 2, name: "Välj reflektions- och ljusnivå", text: "Hög reflektion (40–65 %) ger starkast envägs-spegeleffekt. Hög VLT (40–60 %) bevarar utsikten bäst inifrån." },
    { "@type": "HowToStep", position: 3, name: "Välj färg / utseende från utsidan", text: "Silver = klassisk spegel. Brons = varmare ton. Neutralgrå = diskret. Vi visar prover på plats." },
    { "@type": "HowToStep", position: 4, name: "Anlita auktoriserad 3M-installatör", text: "Garanti och hållbarhet kräver certifierad montör — annars gäller inte 3M:s 10-årsgaranti." },
    { "@type": "HowToStep", position: 5, name: "Kombinera vid behov", text: "Vid känsliga rum (sovrum, badrum) kan spegelfilm kompletteras med frostad dekorfilm eller lätt gardin för 100 % kvällsinsynsskydd." },
  ],
};

export const Route = createFileRoute("/insynsskydd-fonster")({
  head: () => ({
    meta: [
      { title: "Insynsskydd fönster — solfilm som låter dig se ut men inte in | BBN Nordic" },
      {
        name: "description",
        content:
          "Insynsskydd med fönsterfilm: spegelfilm/solfilm som blockerar insyn dagtid men bevarar utsikten. Ärlig guide om dag/natt-skillnad, priser från 750 kr/m² och fri offert.",
      },
      { property: "og:title", content: "Insynsskydd för fönster — se ut men inte in" },
      { property: "og:description", content: "Hur spegelfilm och solfilm fungerar som insynsskydd, jämförelse dag/natt, priser och rådgivning. Officiella auktoriserade 3M-installatörer i Sverige." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://bbnnordic.se/insynsskydd-fonster" },
      { property: "og:image", content: IMAGES.dekorfolieHero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/insynsskydd-fonster" }],
    scripts: [
      ...buildGuideSchemas(meta),
      { type: "application/ld+json", children: JSON.stringify(howToSchema) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      {/* Hero med direkt-svar */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy overflow-hidden">
        <HeroBgImage src={BBN_PHOTOS.dekorOffice} alt="Spegelfilm och insynsskydd på kontorsfönster — dagtidsvisning" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              <Eye size={14} />
              <span>Guide · Insynsskydd</span>
              <span className="text-cream/70">·</span>
              <span className="text-cream/80">{meta.readTimeMin} min läsning</span>
            </div>
            <h1 className="font-display text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Insynsskydd för fönster — <em className="italic text-gold">se ut men inte in</em>
            </h1>
            <LandingHeroConversion />

            <aside className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 backdrop-blur-sm p-5 md:p-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold-bright font-semibold mb-3">
                <Sparkles size={12} /> Kort svar
              </div>
              <p className="text-cream/95 text-base md:text-lg leading-relaxed">
                <strong className="text-gold-bright">Spegelfilm</strong> och insynsskyddande solfilm
                fungerar som en envägsspegel <strong className="text-gold-bright">dagtid</strong>:
                utsidan blir reflekterande medan du behåller fri utsikt inifrån. Tekniken bygger
                på att utomhusljuset normalt är 10–100× starkare än ditt inomhusljus.
                <strong className="text-gold-bright"> Kvällstid</strong>, när du tänt lampor och
                det är mörkt ute, vänds effekten — då behövs gardin, persienn eller frostad
                dekorfilm för komplett insynsskydd. Pris: 750–1 600 kr/m² installerat.
              </p>
            </aside>

          </Reveal>
        </div>
      </section>

      <PostHeroConversion />

      {/* Dag vs natt — visuell split */}
      <section className="py-16 md:py-20 bg-card/30 border-y border-border">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Så fungerar det</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Insynsskyddet beror på ljuset ute och inne.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl font-light">
              Spegelfilm behöver rätt ljusförhållanden för att skydda mot insyn. Vill du ha ett permanent insynsskydd behöver du också avstå från utsikten.
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Reveal>
              <article className="h-full rounded-3xl border border-gold/30 bg-card/60 p-7 md:p-9 hover:border-gold/60 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-gold/15 p-2.5"><Sun size={22} className="text-gold" /></div>
                  <h3 className="text-2xl font-display text-foreground">Dagtid — full envägs­effekt</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Cirka 07–19 i Sverige (året om — även mulna vinterdagar). Utomhusljuset är starkare än ditt inomhusljus, vilket gör att spegelfilmens reflektion dominerar utsidan. Den som tittar in ser sin egen spegelbild.
                </p>
                <ul className="mt-5 space-y-2.5 text-sm">
                  {[
                    "Fri utsikt inifrån — färger och kontrast bevaras",
                    "Speglande/reflekterande effekt utifrån",
                    "Värmereduktion 50–65 % som bonus (TSER)",
                    "UV-skydd 99 % skyddar möbler & textilier",
                    "Bländningsreducering på arbetsplatser",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-foreground/80">
                      <CheckCircle2 size={16} className="text-gold mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>

            <Reveal delay={0.1}>
              <article className="h-full rounded-3xl border border-border bg-card/40 p-7 md:p-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-muted/40 p-2.5"><Moon size={22} className="text-muted-foreground" /></div>
                  <h3 className="text-2xl font-display text-foreground">När det är ljusare inne än ute</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  När det är mörkt ute och dina lampor är tända blir inomhus­ljuset starkare än utomhus­ljuset. Den speglande effekten flyttas då till insidan av rutan — och utifrån kan man se in. Det här gäller alla speglande filmer.
                </p>
                <ul className="mt-5 space-y-2.5 text-sm">
                  {[
                    "Spegelbilden hamnar inåt och det går att se in",
                    "Lösning 1: dra för gardin eller persienn när lampan tänds",
                    "Lösning 2: kombinera med frostad dekorfilm som alltid är opak",
                    "Lösning 3: dämpad inomhusbelysning minskar effekten markant",
                    "I rum som bara används i dagsljus (kontor, vardagsrum) är detta sällan ett problem",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-foreground/80">
                      <AlertTriangle size={16} className="text-muted-foreground mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-8 rounded-2xl border border-gold/20 bg-gold/5 p-5 md:p-6 flex items-start gap-4">
              <Lightbulb size={20} className="text-gold-bright shrink-0 mt-0.5" />
              <p className="text-sm md:text-base text-foreground/85 leading-relaxed">
                <strong>Vårt råd:</strong> För sovrum och badrum rekommenderar vi spegelfilm tillsammans med frostad dekorfilm eller gardin, så att insynsskyddet fungerar även när det är mörkt ute. För kontor, vardagsrum, butiksfönster och balkongdörrar räcker spegelfilm i 95 % av fallen.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Privat vs företag */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">För vem</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Insynsskydd för bostäder och arbetsplatser.
            </h2>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Reveal>
              <article className="h-full rounded-3xl border border-gold/20 bg-card/60 p-7 md:p-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-gold/15 p-2.5"><Home size={22} className="text-gold" /></div>
                  <h3 className="text-2xl font-display text-foreground">Privatbostäder</h3>
                </div>
                <ul className="mt-2 space-y-2.5 text-sm">
                  {[
                    "Markplans­lägenheter & radhus mot gata eller gångväg",
                    "Villor med grannar nära tomtgränsen",
                    "Balkongdörrar och uteplats­glas",
                    "Sovrum & arbetsrum mot insynsexponerad sida",
                    "Bostadsrätt — invändig film kräver inget styrelsegodkännande",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-foreground/80">
                      <CheckCircle2 size={16} className="text-gold mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <Link to="/offert" search={{ produkt: "Solfilm" }} className="mt-6 inline-flex items-center gap-2 text-gold-bright font-semibold hover:text-gold transition-colors">
                  Offert för bostad <ArrowRight size={16} />
                </Link>
              </article>
            </Reveal>

            <Reveal delay={0.1}>
              <article className="h-full rounded-3xl border border-gold/20 bg-card/60 p-7 md:p-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-gold/15 p-2.5"><Building2 size={22} className="text-gold" /></div>
                  <h3 className="text-2xl font-display text-foreground">Kontor & företag</h3>
                </div>
                <ul className="mt-2 space-y-2.5 text-sm">
                  {[
                    "Kontorslandskap mot gata — anställdas integritet vid skärmen",
                    "Konferens- & styrelserum — diskretion utan att stänga av ljuset",
                    "Mottagningar & receptioner i markplan",
                    "Butiker som vill skydda lager- eller personalytor",
                    "Vårdmiljö & advokatkontor — sekretess vid samtal",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-foreground/80">
                      <CheckCircle2 size={16} className="text-gold mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <Link to="/offert" search={{ produkt: "Solfilm" }} className="mt-6 inline-flex items-center gap-2 text-gold-bright font-semibold hover:text-gold transition-colors">
                  Offert för företag <ArrowRight size={16} />
                </Link>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <GuideBody>
        <h2>Hur fungerar fönsterfilm som insynsskydd?</h2>
        <p>
          Spegelfilm och insynsskyddande solfilm är optiska produkter som
          utnyttjar <strong>skillnaden i ljusstyrka mellan utsida och insida</strong>.
          Filmen har en tunn metallisk eller keramisk beläggning (vanligen aluminium,
          silver eller en keramisk hybrid) som reflekterar 40–65 % av det inkommande
          ljuset. Resultatet blir att utsidan domineras av reflektionen av omgivningen
          — himmel, byggnader, träd — istället för det svaga ljus som tränger ut från
          ditt rum.
        </p>
        <p>
          Inifrån ser du fortfarande rakt ut, eftersom dina ögon är anpassade till det
          svagare inomhusljuset och uppfattar utomhusljuset (även genom en mörkare
          film) som tillräckligt starkt för en klar bild.
        </p>

        <h2>Jämförelse: spegelfilm, klar solfilm och frostad dekorfilm</h2>
        <div className="not-prose overflow-x-auto rounded-2xl border border-gold/15 my-6">
          <table className="w-full text-sm">
            <caption className="sr-only">Jämförelse av insynsskyddande filmtyper.</caption>
            <thead>
              <tr className="bg-navy-light/40 border-b border-gold/15">
                <th scope="col" className="text-left p-4 font-semibold text-foreground">Egenskap</th>
                <th scope="col" className="text-left p-4 font-semibold text-gold-bright">
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={14} /> Spegelfilm</span>
                </th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Klar solfilm</th>
                <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Frostad dekorfilm</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Insynsskydd dagtid", "Mycket starkt (envägsspegel)", "Svagt", "Totalt (opak)"],
                ["Insynsskydd kväll/natt", "Svagt — kräver gardin", "Inget", "Totalt (opak)"],
                ["Utsikt inifrån bevarad", "Ja (lite mörkare)", "Ja, helt", "Nej — opak"],
                ["Värme/UV-reduktion", "50–65 % värme, 99 % UV", "30–55 % värme, 99 % UV", "Marginellt"],
                ["Utseende utifrån", "Speglande (silver/brons/grå)", "Nästan osynlig", "Mjölkvit / frostad"],
                ["Typiska rum", "Vardagsrum, kontor, balkong, butik", "Hela hem för komfort", "Badrum, mötesrum, glasdörrar"],
                ["Pris/m² installerat", "750–1 600 kr", "950–2 200 kr (3M Prestige)", "650–1 200 kr"],
              ].map(([f, sp, kl, fr], i) => (
                <tr key={f} className={i % 2 ? "bg-navy-light/10" : ""}>
                  <th scope="row" className="text-left p-4 text-foreground font-medium">{f}</th>
                  <td className="p-4 text-gold-bright font-medium">{sp}</td>
                  <td className="p-4 text-muted-foreground">{kl}</td>
                  <td className="p-4 text-muted-foreground">{fr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          <strong>Vår tumregel:</strong> Vill du behålla utsikten → spegelfilm. Vill du
          ha 100 % opacitet i ett rum där utsikten inte spelar roll (badrum, mötesrum,
          glasdörrar) → frostad dekorfilm. Vill du bara ha värmereduktion och inte bryr
          dig om insyn → klar solfilm typ 3M Prestige.
        </p>

        <h2>Så ser spegelfilmen ut från utsidan</h2>
        <p>
          Färgen på reflektionen beror på filmens metalliska beläggning. De tre
          vanligaste är:
        </p>
        <ul>
          <li><strong>Silver / krom.</strong> Klassisk envägsspegel med neutralt, modernt utseende. Vanligast på kontorshus och butiker.</li>
          <li><strong>Brons / amber.</strong> Varmare ton. Smälter bättre in på äldre tegelfasader och bruna träfasader.</li>
          <li><strong>Neutralgrå / charcoal.</strong> Diskret och något mörkare. Populärast på moderna villor där man inte vill att fasaden ska "blixtra".</li>
        </ul>
        <p>
          Vi tar med fysiska prover ut till dig så du ser exakt hur fasaden påverkas
          innan du beställer.
        </p>

        <h2>Vad kostar insynsskydd på fönster?</h2>
        <p>
          Skåne 2026, inklusive material, installation och 10 års produkt­garanti:
        </p>
        <ul>
          <li><strong>Lägenhet, balkongdörr + 2 fönster (≈4 m²):</strong> 3 500–6 000 kr</li>
          <li><strong>Villa, hela bottenvåningen mot gata (≈10 m²):</strong> 8 000–14 000 kr</li>
          <li><strong>Kontorslandskap (≈40 m²):</strong> 32 000–55 000 kr</li>
          <li><strong>Butiks- eller showroomfönster (≈25 m²):</strong> 20 000–38 000 kr</li>
        </ul>

        <h2>Vanliga frågor om insynsskydd</h2>
        <CheckList
          items={[
            "\"Insynsskydd fönster se ut inte in\" — Ja, dagtid. Spegelfilm är produkten.",
            "\"Fönsterfilm spegel\" / \"spegelfilm fönster\" — Samma produktfamilj som ovan.",
            "\"Fönsterfilm insynsskydd genomskinlig\" — Möjligt med VLT 40–60 %, men inte 100 % klar.",
            "\"Insynsskydd dygnet runt\" — Kräver frostad dekorfilm eller spegelfilm + gardin.",
            "\"Påverkas hemförsäkringen?\" — Nej, invändig film påverkar inte fönstrets garanti eller försäkring.",
            "\"Kan jag montera själv?\" — Tekniskt ja, men 3M:s 10-årsgaranti gäller endast med auktoriserad installatör.",
          ]}
        />

        <h2>Är spegelfilm rätt för dig?</h2>
        <p>
          Spegelfilm passar dig som:
        </p>
        <ul>
          <li>Vill behålla utsikten och dagsljuset, men slippa känslan av att vara "uttittad"</li>
          <li>Bor i markplan, radhus med nära grannar, eller kontor med insynsexponering</li>
          <li>Vill ha både insynsskydd <em>och</em> värmereduktion i samma installation</li>
          <li>Tycker att gardiner blockerar för mycket ljus och gör rummet mörkare</li>
        </ul>
        <p>
          Spegelfilm passar sämre om du vill ha full avskärmning även när det är mörkt ute och
          inte vill dra för en gardin. Då passar frostad dekorfilm bättre. Den passar inte heller
          om fasaden måste vara helt reflektionsfri av estetiska eller kulturhistoriska skäl.
        </p>
      </GuideBody>

      {/* Stor CTA — prefyllt formulär */}
      <section className="bg-navy py-16 md:py-20 border-y border-gold/20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold-bright font-semibold mb-5">
            <EyeOff size={14} /> Auktoriserad 3M-installatör · 10 års garanti
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-cream mb-5">
            Fråga oss vilken film som ger bäst insynsskydd för just dina fönster
          </h2>
          <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Berätta vilka rum och vilken sida av huset det gäller, och under vilka
            ljusförhållanden du behöver insynsskydd. Då kan vi föreslå film och ge dig
            ett fast pris. Formuläret är förvalt för <strong className="text-gold-bright">Solfilm</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/offert"
              search={{ produkt: "Solfilm" }}
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
            >
              Begär kostnadsfri rådgivning <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-6 py-4 rounded-full font-semibold hover:bg-gold/10 transition min-h-12"
            >
              <Phone size={16} /> 0771-502 502
            </a>
          </div>
          <p className="mt-6 text-xs text-cream/80">
            3 500+ nöjda kunder · AAA-kreditvärdighet · Auktoriserad 3M-installatör
          </p>
        </div>
      </section>

      <GuideFaq items={meta.faqs ?? []} />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
